import type { ProColumns } from '@ant-design/pro-components'
import { Link } from '@tanstack/react-router'
import { Tag, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import { LockKeyhole, ShieldCheck } from 'lucide-react'
import { TableRowActions } from '@/components/table-row-actions.tsx'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import type { useMemberCreatorOptions } from '@/features/access/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'
import type { RoleListSearch } from './role-list-search.ts'
import type { RoleRow } from './types.ts'

type Options = {
  search: RoleListSearch
  creators: ReturnType<typeof useMemberCreatorOptions>
  statusPending: boolean
  fixActions: boolean
  onSetEnabled: (rows: RoleRow[], enabled: boolean) => void
}

export function getRoleListColumns({
  search,
  creators,
  statusPending,
  fixActions,
  onSetEnabled
}: Options): ProColumns<RoleRow>[] {
  const sortOrder = search.sortOrder === 'asc' ? 'ascend' : 'descend'
  return [
    {
      title: '角色名称',
      dataIndex: 'keyword',
      hideInTable: true,
      order: 5,
      fieldProps: { placeholder: '搜索名称或编码', allowClear: true }
    },
    {
      title: '角色',
      dataIndex: 'name',
      search: false,
      width: '13rem',
      sorter: true,
      sortOrder: search.sortField === 'name' ? sortOrder : null,
      render: (_, role) => (
        <div className="flex min-w-0 items-center gap-3 py-1">
          <span className="bg-accent/10 text-accent flex size-9 shrink-0 items-center justify-center rounded-lg">
            <ShieldCheck className="size-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <Tooltip title={role.description || role.name}>
                <Link
                  className="text-content! hover:text-accent! truncate font-medium"
                  to="/system-management/roles/$roleId"
                  params={{ roleId: role.id }}
                >
                  {role.name}
                </Link>
              </Tooltip>
              {role.isProtected && (
                <Tooltip title="系统内置角色，不支持修改或停用">
                  <LockKeyhole className="text-content-soft size-3.5 shrink-0" aria-label="系统内置角色" />
                </Tooltip>
              )}
            </div>
            <div className="text-content-soft mt-1 truncate font-mono text-xs" title={role.code}>
              {role.code}
            </div>
          </div>
        </div>
      )
    },
    {
      title: '状态',
      dataIndex: 'isEnabled',
      width: '5rem',
      order: 4,
      valueType: 'select',
      fieldProps: { placeholder: '全部状态', allowClear: true },
      valueEnum: { true: { text: '启用', status: 'Success' }, false: { text: '停用', status: 'Default' } },
      render: (_, role) => (
        <Tag className="m-0!" variant="filled" color={role.isEnabled ? 'success' : 'default'}>
          {role.isEnabled ? '启用' : '停用'}
        </Tag>
      )
    },
    {
      title: '权限',
      dataIndex: 'permissionCount',
      search: false,
      width: '5rem',
      render: (_, role) => (
        <span className="text-content font-medium tabular-nums">
          {role.permissionCount}
          <span className="text-content-soft ml-1 text-xs font-normal">项</span>
        </span>
      )
    },
    {
      title: '关联账号',
      dataIndex: 'accountCount',
      search: false,
      width: '6rem',
      render: (_, role) => (
        <div>
          <Link
            className="font-medium tabular-nums"
            to="/system-management/roles/$roleId"
            params={{ roleId: role.id }}
            search={{ tab: 'accounts' }}
          >
            {role.accountCount}
            <span className="ml-1 text-xs font-normal">个账号</span>
          </Link>
          <div className="text-content-soft mt-1 text-xs">{role.activeAccountCount} 个活跃</div>
        </div>
      )
    },
    {
      title: '创建人',
      dataIndex: 'creatorMemberId',
      hideInTable: true,
      order: 2,
      valueType: 'select',
      fieldProps: { ...proRemoteSearch(creators.onSearch), options: creators.options, loading: creators.isLoading }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAtRange',
      hideInTable: true,
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateTimeRange',
      order: 1
    },
    {
      title: '创建信息',
      tooltip: '创建人及创建时间，点击按创建时间排序',
      dataIndex: 'createdAt',
      search: false,
      width: '10.5rem',
      sorter: true,
      sortOrder: search.sortField === 'createdAt' ? sortOrder : null,
      render: (_, role) => (
        <div className="whitespace-nowrap">
          {role.creator ? (
            <UserAvatarName name={role.creator.name} avatar={role.creator.avatar} isSystem={role.creator.isSystem} />
          ) : (
            <span className="text-content-muted text-sm">系统初始化</span>
          )}
          <div className="text-content-soft mt-1 text-xs tabular-nums">
            <time dateTime={dayjs(role.createdAt).toISOString()}>
              {dayjs(role.createdAt).format('YYYY-MM-DD HH:mm')}
            </time>
          </div>
        </div>
      )
    },
    {
      title: '角色 ID',
      dataIndex: 'id',
      width: '8.5rem',
      order: 3,
      fieldProps: { placeholder: '输入完整 UUID 精确查询' },
      render: (_, role) => (
        <Typography.Text
          className="text-content-soft! font-mono text-xs whitespace-nowrap"
          copyable={{ text: role.id, tooltips: ['复制完整 ID', '已复制'] }}
        >
          <Tooltip title={role.id}>{role.id.slice(0, 8)}…</Tooltip>
        </Typography.Text>
      )
    },
    {
      title: '操作',
      valueType: 'option',
      width: '7.5rem',
      fixed: fixActions ? 'right' : undefined,
      className: 'bg-surface!',
      render: (_, role) => (
        <TableRowActions
          actions={[
            {
              key: 'edit',
              label: role.isProtected ? '查看配置' : '配置权限',
              render: (
                <Link to="/system-management/roles/$roleId" params={{ roleId: role.id }}>
                  {role.isProtected ? '查看配置' : '配置权限'}
                </Link>
              )
            },
            ...(!role.isProtected
              ? [
                  {
                    key: 'status',
                    label: role.isEnabled ? '停用' : '启用',
                    danger: role.isEnabled,
                    disabled: statusPending,
                    onClick: () => onSetEnabled([role], !role.isEnabled)
                  }
                ]
              : [])
          ]}
        />
      )
    }
  ]
}
